import { useCallback, useEffect, useState } from "react";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { buildStationMonitorRows, summarizeStationMonitorRows } from "../util/stationMonitor";

// #2827 (scribe-resilience workstream, G12 part B) — fetches BOTH sources the
// station monitor and the dashboard strip need (GET /presence, GET
// /show/:showId/station-snapshot), joins them with the pure
// buildStationMonitorRows (util/stationMonitor.js), and derives the
// show-wide summary from those SAME rows. One hook, one fetch, two
// renderers (StationMonitor.jsx's full table and the dashboard strip) — so
// a bug in the join can't disagree with itself between the two views.
//
// Not polled like usePresence/ShowDashboard: this is a tent-glances-at-it
// page, not a live-updating ring display, so it fetches on mount/showId
// change and on an explicit refetch() rather than an interval.
export function useStationMonitor(showId) {
  const scoreApi = useScoreApi();
  const [state, setState] = useState({ rows: [], summary: null, loading: true, error: null });

  const refetch = useCallback(async () => {
    if (!showId) {
      setState({ rows: [], summary: null, loading: false, error: null });
      return;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const [presenceList, snapshotList] = await Promise.all([
        unwrap(await scoreApi.presence.listPresence(showId)),
        unwrap(await scoreApi.stationSnapshot.listStationSnapshots(showId)),
      ]);
      const rows = buildStationMonitorRows({ presenceList, snapshotList });
      const summary = summarizeStationMonitorRows(rows);
      setState({ rows, summary, loading: false, error: null });
    } catch (err) {
      setState({ rows: [], summary: null, loading: false, error: err });
    }
  }, [scoreApi, showId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // #2828 (scribe-resilience workstream, G12 part B AC1/AC2) — compares one
  // station's ship log against the server and stores the fresh disagreement
  // list. Throws on failure (404: station has never uploaded a snapshot) so
  // the page can show it per-row rather than a global error; refetches on
  // success so the row's reconcile state (disagreementCount, reconciledAt)
  // comes back through the SAME join the table already renders from.
  const reconcileStation = useCallback(
    async (deviceId, scribeSub) => {
      unwrap(await scoreApi.stationSnapshot.reconcileStation(showId, deviceId, scribeSub));
      await refetch();
    },
    [scoreApi, showId, refetch],
  );

  // AC3 — stamps who/when reviewed a station's most recent reconcile
  // result. `reconciledAt` is the value the CALLER actually saw rendered
  // (the row's own reconcile.reconciledAt, never re-derived here) — the
  // optimistic lock (judge-early-1 fix 4) 409s if a fresh reconcile landed
  // since. Left for the caller to catch: a 409 means the row the operator
  // was looking at is now stale, and refetch() below would otherwise
  // silently replace it out from under an error message.
  const markReviewed = useCallback(
    async (deviceId, scribeSub, reconciledAt) => {
      unwrap(
        await scoreApi.stationSnapshot.markStationReconcileReviewed(showId, deviceId, scribeSub, {
          reconciledAt,
        }),
      );
      await refetch();
    },
    [scoreApi, showId, refetch],
  );

  return { ...state, refetch, reconcileStation, markReviewed };
}

export default useStationMonitor;
